import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Bell,
  ClipboardList,
  Lock,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  Sparkles,
  UserRound,
  Wrench,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { BrandLogo, Input, Select } from '@components/ui'
import { ExitIntentPopup } from '@components/marketing/ExitIntentPopup'
import { TestimonialsSection } from '@components/marketing/TestimonialsSection'
import { isMobileOrTablet, useExitIntent } from '@hooks/useExitIntent'
import {
  submitWorkshopSignup,
  WORKSHOP_SIGNUP_SUCCESS_MESSAGE,
} from '@services/workshopSignup'
import { trackLandingPageView } from '@services/landingAnalytics'
import { markExitPopupSeen, markLandingConverted } from '@utils/landingSession'
/**
 * Landing page FIXDAUTO — rota `/`, pública (sem sessão).
 * A homepage da aplicação (chat da receção) é `/homepage` e exige login (rececionista ou administrador).
 */

/** URL do logotipo e assets em public (respeita VITE base path). */
function publicUrl(path: string): string {
  const base = import.meta.env.BASE_URL || '/'
  const p = path.startsWith('/') ? path : `/${path}`
  if (base === '/') return p
  return `${base.replace(/\/$/, '')}${p}`
}



const PLAN_PRICES = {
  monthly: { base: 29, pro: 65, proUpgrade: 36 },
  annual: { base: 24, pro: 54, proUpgrade: 30 },
} as const

function scrollToRegistoSection(e: MouseEvent<HTMLElement>) {
  e.preventDefault()
  const target = document.getElementById('registo')
  if (!target) return
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const firstField = target.querySelector<HTMLElement>('input, select, textarea')
  if (firstField) {
    window.setTimeout(() => firstField.focus({ preventScroll: true }), 400)
  }
}

const PLAN_BASE_FEATURES_INCLUDED = [
  'Qualquer funcionário cria folhas de obra em 30 segundos, sem erros, sem formação',
  'Historial completo de cada cliente e viatura, nunca mais vai perder informação',
  'Marcações online sem conflitos, a agenda da oficina sempre organizada',
  'Sabe em tempo real quanto tempo cada mecânico gasta em cada trabalho',
  'Até 5 utilizadores, toda a equipa com acesso ao mesmo tempo',
  '5 GB de espaço para usar à sua vontade',
  'Fotos e vídeos associados às folhas de obra',
  'Migração do seu software atual completamente gratuita e em menos de 48h',
] as const

const PLAN_BASE_FEATURES_EXCLUDED = [
  'Assistente técnico para o apoiar em todas as etapas',
  'Assistente técnico que sugere serviços antes de o cliente pedir',
] as const

const PLAN_PRO_DIFF_FEATURES = [
  'Tem um assistente técnico que o apoia em todas as fases do serviço',
  'Lembretes para revisões de cada cliente, nunca mais perca dinheiro por esquecimento',
  'IA que apoia na criação de folhas de obra automática com perguntas que o ajudam a chegar a um melhor diagnóstico',
] as const

const PLAN_PRO_EXTRA_FEATURES = [
  'Tudo incluído no plano Base',
  'Suporte prioritário, resposta em menos de 2 horas',
  'Formação da equipa incluída, caso necessário',
  'Acesso antecipado a novas funcionalidades',
  '15 GB de fotos e vídeos associados às folhas de obra',
] as const

const FEATURE_BULLETS: Record<string, readonly string[]> = {
  '01': [
    'A rececionista cria a folha de obra em 30 segundos, sem erros e sem formação prévia',
    'Centralização da informação para o serviço num só painel',
    'Apoio técnico de uma IA para o ajudar a trabalhar mais rapidamente',
  ],
  '02': [
    'Sugere possíveis diagnósticos para avarias, de acordo com históricos passados',
    'Mecânicos juniores trabalham autonomamente, sem formação necessária',
    'Organiza a informação da folha de obra e dá-lhe dicas ao longo do serviço',
  ],
  '03': [
    'Quando o cliente liga, já sabe tudo sobre o carro dele em segundos, sem ir procurar papéis',
    'Historial de tudo o que foi feito anteriormente, não começa do 0',
    'Alerta-o quando um cliente não volta há mais de X meses, antes que a concorrência o conquiste',
  ],
  '04': [
    'Nenhum carro sai da oficina sem próxima revisão marcada, o sistema lembra-se por si',
    'Alerta automático por email ou SMS quando a revisão se aproxima, sem fazer nada',
    'Sabe quantas revisões estão em atraso e quanto dinheiro isso representa para a sua oficina',
  ],
}

const LANDING_FAQS = [
  {
    q: 'Tenho de assinar um contrato anual?',
    a: 'Não. O plano mensal não tem qualquer compromisso, cancele quando quiser, sem penalizações. O plano anual oferece um desconto equivalente a 2 meses grátis, mas é uma opção, nunca uma obrigação.',
  },
  {
    q: 'A migração do meu software atual é mesmo gratuita?',
    a: 'Sim, 100% gratuita. A nossa equipa trata de migrar os dados do seu software atual sem custo adicional. A maioria das migrações fica concluída em menos de 48 horas.',
  },
  {
    q: 'E se eu não gostar? Perco o dinheiro?',
    a: 'Os primeiros 30 dias são grátis, sem cartão de crédito e sem compromisso. Se não gostar, não paga nada e os seus dados são exportados quando quiser.',
  },
  {
    q: 'Preciso de ter jeito para tecnologia?',
    a: 'Não. A FIXDAUTO foi desenhada especificamente para oficinas em conjunto com mecânicos. A maioria começa a trabalhar autonomamente no próprio dia.',
  },
  {
    q: 'O suporte é em português e rápido?',
    a: 'Sim. Somos uma marca portuguesa e o suporte é feito através de telefone, Whatsapp ou email. No Base respondemos em menos de 4h. No Pro a resposta é em menos de 2h.',
  },
] as const

type HeroVariant = {
  headline: ReactNode
  description: ReactNode
  /** Headline mais longo — tamanho reduzido para alinhar visualmente com as outras variantes */
  headlineClassName?: string
}

const HERO_VARIANTS: readonly HeroVariant[] = [
  {
    headline: (
      <>
        <span className="text-primary-400">Assistente de IA</span> como mais um funcionário
      </>
    ),
    description: (
      <>
        A <strong className="font-medium text-white">FIXDAUTO</strong> foi criada em conjunto com mecânicos,
        com um assistente de IA que atua como mais um funcionário na sua oficina.
        Oferecemos migração gratuita do seu software atual para o nosso.
      </>
    ),
  },
  {
    headline: (
      <>
        Cuide da <span className="text-primary-400">sua oficina</span> sem papel, sem formação e a um preço acessível
      </>
    ),
    description: (
      <>
        A <strong className="font-medium text-white">FIXDAUTO</strong> foi criada com mecânicos portugueses para
        resolver o que lhe tira tempo todos os dias, folhas de obra, histórico de clientes, marcações e muito mais.
        Comece em minutos, sem mudar a forma como trabalha.
      </>
    ),
    headlineClassName: 'text-3xl sm:text-4xl lg:text-[2.75rem]',
  },
  {
    headline: (
      <>
        O software para oficinas <span className="text-primary-400">mais completo</span> do mercado. Por{' '}
        <span className="text-primary-400">29€</span> por mês
      </>
    ),
    description: (
      <>
        Folhas de obra digitais, assistente de IA, histórico de clientes e lembretes automáticos, tudo numa
        plataforma criada com mecânicos portugueses. Sem formação. Sem contratos. Experimente 30 dias sem pagar nada.
      </>
    ),
  },
]

function getRandomHeroVariantIndex(): number {
  return Math.floor(Math.random() * HERO_VARIANTS.length)
}

export function PublicLandingPage() {
  // Cross-browser video: prefer mp4, keep mov as last fallback (Safari).
  const demoVideoSrcMp4 = publicUrl('/demo-fixdauto-mini.mp4')
  const demoVideoSrcMov = publicUrl('/demo-fixdauto-mini.mov')
  const [heroVariantIndex] = useState(getRandomHeroVariantIndex)
  const heroVariant = HERO_VARIANTS[heroVariantIndex]
  const [exitPopupOpen, setExitPopupOpen] = useState(false)
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [shouldRenderDemoVideo, setShouldRenderDemoVideo] = useState(false)
  const [demoVideoReady, setDemoVideoReady] = useState(false)
  const [demoVideoError, setDemoVideoError] = useState(false)
  const demoVideoRef = useRef<HTMLVideoElement | null>(null)

  useExitIntent({
    onTrigger: () => {
      // Desktop: marcar visto ao abrir (comportamento original).
      // Mobile/tablet: só marcar ao fechar ou submeter, para não perder a oportunidade.
      if (!isMobileOrTablet()) {
        markExitPopupSeen()
      }
      setExitPopupOpen(true)
    },
  })

  useEffect(() => {
    void trackLandingPageView(heroVariantIndex)
  }, [heroVariantIndex])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    // Debug / QA: permite forçar o popup via URL, mesmo que já tenha sido visto na sessão.
    if (params.get('exitPopup') === '1') {
      setExitPopupOpen(true)
      return
    }
  }, [])

  useEffect(() => {
    const nodes = document.querySelectorAll<HTMLElement>('[data-reveal]')
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (!entry.isIntersecting) return
          const el = entry.target as HTMLElement
          setTimeout(() => {
            el.classList.add('reveal-in')
          }, i * 60)
          observer.unobserve(el)
        })
      },
      { threshold: 0.08 }
    )
    nodes.forEach((n) => observer.observe(n))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // Lazy-load pesado: só montar o <video> depois da página estar pronta.
    const start = () => {
      const w = window as any
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(() => setShouldRenderDemoVideo(true), { timeout: 2500 })
      } else {
        setTimeout(() => setShouldRenderDemoVideo(true), 1200)
      }
    }

    if (document.readyState === 'complete') {
      start()
      return
    }

    window.addEventListener('load', start, { once: true })
    return () => window.removeEventListener('load', start)
  }, [])

  useEffect(() => {
    if (!shouldRenderDemoVideo || demoVideoError) return
    const v = demoVideoRef.current
    if (!v) return

    // Some browsers are picky about autoplay even when muted; try to start
    // playback once mounted, but ignore failures (we keep the UI usable).
    const tryPlay = async () => {
      try {
        await v.play()
      } catch {
        // ignore
      }
    }
    void tryPlay()
  }, [shouldRenderDemoVideo, demoVideoError])

  useEffect(() => {
    if (!videoModalOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVideoModalOpen(false)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [videoModalOpen])

  const DemoVideo = (
    <div
      className={`relative overflow-hidden rounded-xl border border-white/10 bg-secondary-950/40 shadow-2xl ${
        demoVideoError ? '' : 'cursor-zoom-in'
      }`}
      role={demoVideoError ? undefined : 'button'}
      tabIndex={demoVideoError ? undefined : 0}
      aria-label={demoVideoError ? 'Vídeo de demonstração' : 'Abrir vídeo em ecrã maior'}
      onClick={() => {
        if (demoVideoError) return
        setVideoModalOpen(true)
      }}
      onKeyDown={(e) => {
        if (demoVideoError) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setVideoModalOpen(true)
        }
      }}
    >
      <div className="aspect-[16/10] w-full">
        {shouldRenderDemoVideo && !demoVideoError ? (
          <>
            {!demoVideoReady ? (
              <div
                className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-secondary-900 via-[#17253C] to-secondary-950"
                aria-label="A carregar vídeo"
              >
                <div className="px-6 text-center">
                  <div className="mx-auto mb-3 h-1.5 w-36 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full w-1/2 animate-pulse rounded-full bg-primary-400/50" />
                  </div>
                </div>
              </div>
            ) : null}

            <video
              ref={demoVideoRef}
              className="h-full w-full object-cover"
              preload="metadata"
              muted
              playsInline
              autoPlay
              loop
              controls={false}
              onCanPlay={() => setDemoVideoReady(true)}
              onError={() => setDemoVideoError(true)}
            >
              <source src={demoVideoSrcMp4} type="video/mp4" />
              {/* Safari / iOS fallback (older content pipeline). */}
              <source src={demoVideoSrcMov} type="video/quicktime" />
            </video>

            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-end p-3">
              <span className="rounded-full bg-black/45 px-3 py-1 text-xs font-medium text-white/80 backdrop-blur">
                Clique para ampliar
              </span>
            </div>
          </>
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-secondary-900 via-[#17253C] to-secondary-950"
            aria-label={demoVideoError ? 'Falha ao carregar vídeo' : 'A carregar vídeo'}
          >
            <div className="px-6 text-center">
              <div className="mx-auto mb-3 h-1.5 w-36 overflow-hidden rounded-full bg-white/10">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-primary-400/50" />
              </div>
              {demoVideoError ? (
                <p className="mt-3 text-xs text-white/60">
                  O seu navegador não conseguiu reproduzir o vídeo. Para suportar Chrome/Firefox, adicione
                  <span className="font-medium text-white/80"> demo-fixdauto-mini.mp4</span> na pasta{' '}
                  <span className="font-medium text-white/80">public/</span>.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-secondary-900">
      <nav className="fixed left-0 right-0 top-0 z-[100] flex h-16 w-full max-w-[100vw] items-center justify-between bg-[#17253C] px-3 text-white shadow-none sm:px-4 md:px-6 md:shadow-lg">
        <Link to="/" className="flex h-full shrink-0 items-center gap-2 no-underline transition-opacity hover:opacity-80">
          <BrandLogo className="max-h-10 sm:max-h-12" />
        </Link>
        <div className="ml-auto flex items-center">
          <Link to="/login" className="btn btn-primary rounded-lg px-5 py-2 text-sm font-medium no-underline">
            Entrar
          </Link>
        </div>
      </nav>

      <main className="relative isolate z-0 pt-16">
        {/* Hero, mesmo tom escuro que o login (#17253C / secondary-800) */}
        <section className="relative z-0 overflow-hidden bg-[#17253C] px-5 py-20 sm:px-8 sm:py-28">
          <div
            className="pointer-events-none absolute -right-24 -top-40 z-0 h-[420px] w-[420px] rounded-full bg-primary-500/20 blur-3xl sm:h-[520px] sm:w-[520px]"
            aria-hidden
          />
          <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <div
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary-400/35 bg-primary-500/15 px-3.5 py-1.5 text-xs font-medium tracking-wide text-primary-200"
                data-reveal
              >
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400" />
                Gestão de oficinas, com IA integrada
              </div>
              <h1
                className={`mb-6 font-bold leading-tight tracking-tight text-white ${
                  heroVariant.headlineClassName ?? 'text-4xl sm:text-5xl lg:text-6xl'
                }`}
                data-reveal
              >
                {heroVariant.headline}
              </h1>
              <p className="mb-8 max-w-xl text-base font-light leading-relaxed text-white/70 sm:text-lg" data-reveal>
                {heroVariant.description}
              </p>
              <div className="flex flex-wrap items-center gap-3" data-reveal>
                <a
                  href="#registo"
                  onClick={scrollToRegistoSection}
                  className="btn btn-primary rounded-lg px-7 py-3.5 text-base no-underline"
                >
                  Experimentar grátis
                </a>
                <a
                  href="#produto"
                  className="flex items-center gap-1 text-sm text-white/60 transition-colors hover:text-white"
                >
                  Ver funcionalidades →
                </a>
              </div>
              <div
                className="mt-11 flex flex-col gap-8 border-t border-white/10 pt-10 sm:flex-row sm:items-start sm:justify-between sm:gap-0"
                data-reveal
              >
                <div className="min-w-0 text-center sm:flex-1 sm:text-left">
                  <span className="mt-1 block text-xs leading-snug text-white/50">Desde</span>
                  <span className="block text-2xl font-bold tabular-nums text-white sm:text-3xl">29€/mês</span>
                  <span className="mt-1 block text-xs leading-snug text-white/50">sem compromisso</span>
                </div>
                <div className="min-w-0 text-center sm:flex-1 sm:text-left">
                <span className="mt-1 block text-xs leading-snug text-white/50">Migração</span>
                  <span className="block text-2xl font-bold tabular-nums text-white sm:text-3xl">&lt;2 dias</span>
                  <span className="mt-1 block text-xs leading-snug text-white/50">
                    migração incluída
                  </span>
                </div>
                <div className="min-w-0 text-center sm:flex-1 sm:text-left">
                <span className="mt-1 block text-xs leading-snug text-white/50">Uso</span>
                  <span className="block text-2xl font-bold tabular-nums text-white sm:text-3xl">0h</span>
                  <span className="mt-1 block text-xs leading-snug text-white/50">de formação</span>
                </div>
              </div>
            </div>

            {/* Desktop / widescreen */}
            <div className="hidden lg:block" data-reveal>
              <div className="rounded-2xl border border-secondary-700 bg-secondary-900/80 p-5 shadow-2xl">
                <div className="mb-4 flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400/90" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300/90" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/90" />
                </div>
                {DemoVideo}
              </div>
            </div>
          </div>

          {/* Mobile / tablet: vídeo abaixo do texto */}
          <div className="relative z-10 mx-auto mt-12 max-w-2xl lg:hidden" data-reveal>
            {DemoVideo}
          </div>
        </section>

        <TestimonialsSection title="O que dizem os donos de oficina" animateOnReveal />

        <div className="border-b border-white/10 bg-gradient-to-b from-[#1a2d47] to-secondary-900 px-5 py-8 sm:px-8 sm:py-10">
          <div className="mx-auto max-w-6xl">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
              <Proof
                Icon={Wrench}
                variant="wrench"
                text={
                  <>
                    Criado <strong className="font-medium text-secondary-900">em conjunto com mecânicos</strong>{' '}
                    portugueses
                  </>
                }
              />
              <Proof
                Icon={RefreshCw}
                variant="refresh"
                text={
                  <>
                    <strong className="font-medium text-secondary-900">Migração gratuita</strong> do seu software atual
                  </>
                }
              />
              <Proof
                Icon={MessageCircle}
                variant="message"
                text={
                  <>
                    Suporte com resposta{' '}
                    <strong className="font-medium text-secondary-900">em menos de 2 horas</strong>
                  </>
                }
              />
              <Proof
                Icon={Zap}
                variant="zap"
                text={
                  <>
                    <strong className="font-medium text-secondary-900">Sem formação</strong> inicial necessária
                  </>
                }
              />
            </div>
          </div>
        </div>

        <section className="bg-gradient-to-b from-secondary-900 to-[#17253C] px-5 py-20 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="mb-4 text-xs font-medium uppercase tracking-widest text-primary-300" data-reveal>
              Antes e depois
            </div>
            <h2 className="mb-14 text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl" data-reveal>
              O que muda na sua
              <br />
              oficina mecânica
              <br />
              com a FIXDAUTO
            </h2>
            <div className="grid gap-0.5 overflow-hidden rounded-2xl lg:grid-cols-2" data-reveal>
              <div className="bg-secondary-900/90 p-8 sm:p-10 lg:p-11">
                <div className="mb-7 flex items-center gap-2.5 text-xs font-bold uppercase tracking-wider text-white/40">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/30" />
                  Sem o software
                </div>
                <CheckList negative />
              </div>
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 p-8 sm:p-10 lg:p-11">
                <div className="mb-7 flex items-center gap-2.5 text-xs font-bold uppercase tracking-wider text-white/85">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                  Com a FIXDAUTO
                </div>
                <CheckList />
              </div>
            </div>
          </div>
        </section>

        <section id="produto" className="px-5 py-20 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="mb-4 text-xs font-medium uppercase tracking-widest text-primary-600" data-reveal>
              Funcionalidades
            </div>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-secondary-900 sm:text-4xl" data-reveal>
              Tudo o que a sua oficina
              <br />
              precisa num só painel
            </h2>
            <p className="mb-14 max-w-xl text-secondary-600" data-reveal>
              Desenvolvido com mecânicos e gestores de oficinas portugueses. Cada funcionalidade resolve um problema real
              do dia a dia.
            </p>
            <div className="grid gap-6 sm:grid-cols-2">
              <FeatureCard
                n="01"
                Icon={ClipboardList}
                title="Folhas de obra automáticas"
                bullets={FEATURE_BULLETS['01']}
              />
              <FeatureCard
                n="02"
                Icon={Sparkles}
                title="Assistente técnico com IA"
                bullets={FEATURE_BULLETS['02']}
              />
              <FeatureCard
                n="03"
                Icon={UserRound}
                title="Ficha de cliente e histórico completo"
                bullets={FEATURE_BULLETS['03']}
              />
              <FeatureCard
                n="04"
                Icon={Bell}
                title="Lembretes de revisões e manutenções automáticas"
                bullets={FEATURE_BULLETS['04']}
              />
            </div>
            <div
              className="mt-8 flex flex-col items-center gap-6 rounded-2xl bg-[#17253C] p-8 text-center sm:flex-row sm:text-left lg:p-10"
              data-reveal
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-900/25 ring-2 ring-white/15">
                <Zap className="h-7 w-7" strokeWidth={1.75} aria-hidden />
              </div>
              <div>
                <h3 className="mb-2 text-lg font-bold text-white">Sempre à frente do mercado</h3>
                <p className="text-sm leading-relaxed text-white/55">
                  Lançamos <strong className="font-medium text-primary-200">atualizações regulares</strong> com novas
                  funcionalidades desenvolvidas a partir do feedback das oficinas que usam o software todos os dias.
                </p>
              </div>
            </div>
          </div>
        </section>

        <MidCtaBanner scrollToRegisto={scrollToRegistoSection} />

        <PricingSection scrollToRegisto={scrollToRegistoSection} />

        <LandingFormSection heroVariantIndex={heroVariantIndex} />

        <footer className="flex w-full max-w-[100vw] flex-col items-center gap-8 border-t border-white/10 bg-[#17253C] px-3 py-9 text-white shadow-none sm:px-4 md:flex-row md:items-start md:justify-between md:gap-6 md:px-6 md:shadow-lg">
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-center sm:gap-10 md:items-start md:gap-12">
            <BrandLogo className="max-h-10 shrink-0 opacity-90 sm:max-h-12" />
            <div className="flex flex-col items-center gap-3 sm:items-start">
              <span className="text-xs font-semibold uppercase tracking-wider text-white/45">Contactos</span>
              <a
                href="mailto:equipa.fixdauto@gmail.com"
                className="flex items-center gap-2 text-sm text-white/85 no-underline transition-colors hover:text-primary-300"
              >
                <Mail className="h-4 w-4 shrink-0 text-primary-400" aria-hidden strokeWidth={2} />
                equipa.fixdauto@gmail.com
              </a>
              <a
                href="tel:+351928490194"
                className="flex items-center gap-2 text-sm text-white/85 no-underline transition-colors hover:text-primary-300"
              >
                <Phone className="h-4 w-4 shrink-0 text-primary-400" aria-hidden strokeWidth={2} />
                +351 928 490 194
              </a>
            </div>
          </div>
          <p className="text-center text-xs text-white/40 md:ml-auto md:text-right md:pt-1">
            © {new Date().getFullYear()}, FIXDAUTO, Portugal
          </p>
        </footer>
      </main>

      <ExitIntentPopup
        open={exitPopupOpen}
        heroVariantIndex={heroVariantIndex}
        onClose={() => {
          if (isMobileOrTablet()) {
            markExitPopupSeen()
          }
          setExitPopupOpen(false)
        }}
      />

      {videoModalOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[210] flex items-center justify-center bg-black/80 p-4"
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget) setVideoModalOpen(false)
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Vídeo de demonstração"
                className="relative w-full max-w-5xl overflow-hidden rounded-2xl border border-white/10 bg-[#0a1628] shadow-2xl"
              >
                <button
                  type="button"
                  onClick={() => setVideoModalOpen(false)}
                  className="absolute right-3 top-3 z-10 rounded-lg bg-black/50 p-2 text-white/80 backdrop-blur transition-colors hover:text-white"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>

                <div className="aspect-video w-full bg-black">
                  <video className="h-full w-full" controls autoPlay playsInline preload="metadata">
                    <source src={demoVideoSrcMp4} type="video/mp4" />
                    <source src={demoVideoSrcMov} type="video/quicktime" />
                  </video>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

const proofIconCapsule: Record<
  'wrench' | 'refresh' | 'message' | 'zap',
  string
> = {
  wrench: 'bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200/90',
  refresh: 'bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200/90',
  message: 'bg-sky-100 text-sky-900 ring-1 ring-inset ring-sky-200/90',
  zap: 'bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200/90',
}

function Proof({
  Icon,
  variant,
  text,
}: {
  Icon: LucideIcon
  variant: keyof typeof proofIconCapsule
  text: ReactNode
}) {
  return (
    <div className="flex h-full min-w-0 flex-col rounded-2xl border border-secondary-200/95 bg-white p-4 shadow-md ring-1 ring-black/[0.04] sm:p-4">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${proofIconCapsule[variant]}`}
          aria-hidden
        >
          <Icon className="h-[22px] w-[22px]" strokeWidth={1.65} />
        </span>
        <span className="min-w-0 text-sm leading-snug text-secondary-700">{text}</span>
      </div>
    </div>
  )
}

const BEFORE_NEG_ITEMS = [
  {
    title: 'Folhas de obra feitas por profissionais sem conhecimento técnicos',
    //desc: 'o cliente reclama, perde uma tarde a corrigir e a pedir desculpas',
  },
  {
    title: 'Falta de tempo para formação aos mecânicos',
    //desc: 'e tem de ir à pilha de papéis procurar, à frente de outro cliente',
  },
  {
    title: 'Erros ao diagnosticar e trabalhar avarias e ter de começar do 0',
    //desc: 'nunca mais voltou. Nem sabe que ele existe',
  },
  {
    title: 'Histórico de clientes inexistente',
    //desc: 'porque não há registo, é sempre como se fosse a primeira vez',
  },
  {
    title: 'Clientes a ligar a perguntar “já está pronto?” sem resposta imediata',
    //desc: 'e quais os que lhe fizeram perder tempo',
  },
  {
    title: 'Nenhum controlo sobre o tempo gasto em cada reparação',
    //desc: 'encomendas de emergência que lhe custam o dobro',
  },
  {
    title: 'Falta de lembretes para revisões futuras de clientes',
    //desc: 'o cliente sente que inventaste o preço e vai comparar à concorrência',
  },
] as const

const BEFORE_POS_ITEMS = [
  'Folha de obra criada em 30 segundos, qualquer funcionário faz direito desde o primeiro dia',
  'Historial completo de cada cliente aparece em segundos, sabe tudo antes de atender',
  'Alerta automático quando um cliente não aparece há mais de X meses, antes que se esqueça',
  'A IA sugere serviços e peças com base no histórico, o mecânico júnior trabalha como um sénior',
  'Relatórios dão-lhe os serviços com mais margem, não só os mais frequentes',
  'Stocks em tempo real, sabe o que tem antes de precisar',
  'Orçamento gerado em 30 segundos: preço consistente, profissional, sem inventar',
] as const

function CheckList({ negative }: { negative?: boolean }) {
  if (negative) {
    return (
      <ul className="flex flex-col gap-5">
        {BEFORE_NEG_ITEMS.map((item) => (
          <li key={item.title} className="flex gap-3 text-sm leading-snug text-white/85">
            <span className="shrink-0 pt-0.5 text-red-400/80">✕</span>
            <div>
              <strong className="block font-semibold text-white">{item.title}</strong>
              {/* <span className="text-white/75"> — {item.desc}</span> */}
            </div>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <ul className="flex flex-col gap-5">
      {BEFORE_POS_ITEMS.map((line) => (
        <li key={line} className="flex gap-3 text-sm leading-snug text-white/95">
          <span className="shrink-0 pt-0.5 text-white">✓</span>
          <span>{line}</span>
        </li>
      ))}
    </ul>
  )
}

function MidCtaBanner({ scrollToRegisto }: { scrollToRegisto: (e: MouseEvent<HTMLElement>) => void }) {
  return (
    <section
      className="relative overflow-hidden bg-[#17253C] px-5 py-14 text-center sm:px-8 sm:py-16"
      data-reveal
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-72 w-[600px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/[0.07]"
        aria-hidden
      />
      <div className="relative mx-auto max-w-xl">
        <h3 className="mb-3 text-2xl font-bold leading-tight text-white sm:text-3xl">
          Experimente 30 dias sem pagar nada
        </h3>
        <p className="mb-6 text-base text-white/75">
          Sem cartão de crédito. Sem compromisso.
        </p>
        <a
          href="#registo"
          onClick={scrollToRegisto}
          className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-bold text-primary-700 no-underline transition hover:bg-primary-200 hover:-translate-y-0.5"
        >
          Inscrever de graça →
        </a>
        <p className="mt-3.5 text-sm text-white/55">
          Sem cartão de crédito · Cancele quando quiser
        </p>
      </div>
    </section>
  )
}

function FeatureCard({
  n,
  Icon,
  title,
  bullets,
}: {
  n: string
  Icon: LucideIcon
  title: string
  bullets: readonly string[]
}) {
  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-secondary-100 p-8 transition-transform duration-200 hover:-translate-y-1"
      data-reveal
    >
      <div className="absolute left-0 top-0 h-0.5 w-full origin-left scale-x-0 bg-primary-600 transition-transform duration-300 group-hover:scale-x-100" />
      <span className="absolute right-7 top-6 text-5xl font-extrabold leading-none text-secondary-200">{n}</span>
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-50 to-primary-100/90 text-primary-700 shadow-sm ring-1 ring-primary-200/50">
        <Icon className="h-6 w-6" strokeWidth={1.65} />
      </div>
      <h3 className="mb-3 text-xl font-bold tracking-tight text-secondary-900">{title}</h3>
      <ul className="flex flex-col gap-2.5">
        {bullets.map((line) => (
          <li key={line} className="flex gap-2.5 text-sm leading-snug text-secondary-700">
            <span className="shrink-0 font-bold text-primary-500">→</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PricingSection({ scrollToRegisto }: { scrollToRegisto: (e: MouseEvent<HTMLElement>) => void }) {
  const [annual, setAnnual] = useState(false)
  const prices = annual ? PLAN_PRICES.annual : PLAN_PRICES.monthly
  const { base: basePrice, proUpgrade: proUpgradeDelta } = prices

  return (
    <section id="precos" className="bg-secondary-100 px-5 py-20 sm:px-8 sm:py-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 text-xs font-medium uppercase tracking-widest text-primary-600" data-reveal>
          Preços
        </div>
        <h2 className="mb-4 text-3xl font-bold text-secondary-900 sm:text-4xl" data-reveal>
          Simples como deve ser
        </h2>
        <p className="mb-8 text-secondary-600" data-reveal>
          Dois planos. Preços claros. Sem surpresas no fim do mês.
        </p>

        <div
          className="mb-7 flex flex-wrap items-center gap-4 rounded-2xl border-2 border-primary-200 bg-gradient-to-br from-primary-50 to-primary-100 p-5 sm:p-6"
          data-reveal
        >
          <span className="text-3xl" aria-hidden>
            🎁
          </span>
          <div className="min-w-[200px] flex-1">
            <strong className="block text-base text-primary-900">Comece grátis já hoje. Sem cartão de crédito</strong>
            <span className="mt-1 block text-sm text-primary-800">
              30 dias completos para testar em ambiente real. Se não gostar, não paga nada.
            </span>
          </div>
          <span className="rounded-full bg-primary-600 px-4 py-1.5 text-sm font-bold text-white">30 dias grátis</span>
        </div>

        <div className="mb-8 flex min-h-[4.5rem] flex-col items-center justify-center gap-2" data-reveal>
          <div className="flex items-center gap-3 rounded-full border border-secondary-200 bg-white px-4 py-2 shadow-md">
            <span className={`text-sm font-medium ${!annual ? 'text-secondary-900' : 'text-secondary-400'}`}>
              Mensal
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={annual}
              onClick={() => setAnnual((v) => !v)}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                annual ? 'bg-primary-600' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                  annual ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${annual ? 'text-secondary-900' : 'text-secondary-400'}`}>
              Anual
            </span>
          </div>
          <span
            className={`rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-600 transition-opacity ${
              annual ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden={!annual}
          >
            Poupe 2 meses
          </span>
        </div>

        <div className="grid gap-6 md:grid-cols-2 md:gap-8" data-reveal>
          <div className="flex flex-col rounded-2xl border border-secondary-200 bg-white p-6 shadow-sm sm:p-8">
            <span className="mb-3 inline-flex w-fit rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
              Básico
            </span>
            <h3 className="text-xl font-bold text-secondary-900">FIXDAUTO Base</h3>
            <p className="mt-2 text-sm leading-relaxed text-secondary-600">
            Gestão completa da oficina num só lugar. Deixe de perder tempo com papéis e passe a ter tudo organizado e acessível em segundos.
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-2.5 text-sm font-semibold text-primary-800">
              <span className="text-primary-600" aria-hidden>
                ✓
              </span>
              30 dias grátis — sem cartão de crédito
            </div>
            <div className="mt-5 flex items-baseline gap-0.5">
              <span className="text-5xl font-extrabold tracking-tight text-secondary-900">{basePrice}</span>
              <span className="text-xl font-bold text-primary-600">€</span>
              <span className="text-sm text-secondary-500">/mês</span>
            </div>
            <p
              className={`mt-1 min-h-[18px] text-sm text-primary-600 transition-opacity ${
                annual ? 'opacity-100' : 'opacity-0'
              }`}
            >
              290€/ano (poupa 58€)
            </p>
            <hr className="my-5 border-secondary-100" />
            <PlanFeatureList variant="base" />
            <a
              href="#registo"
              onClick={scrollToRegisto}
              className="btn btn-primary mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold no-underline"
            >
              Testar grátis durante 30 dias
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden strokeWidth={2.25} />
            </a>
            <p className="mt-2 text-center text-xs text-secondary-500">
              Sem cartão de crédito. Cancele quando quiser.
            </p>
          </div>

          <div className="relative flex flex-col overflow-hidden rounded-2xl bg-secondary-800 p-6 shadow-xl ring-2 ring-primary-500/40 sm:p-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary-500/25 blur-2xl" />
            <div className="relative flex flex-1 flex-col">
              <span className="mb-3 inline-flex w-fit rounded-full bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-200">
                ⭐ Recomendado
              </span>
              <h3 className="text-xl font-bold text-white">FIXDAUTO Pro</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">
                Tudo do Base, mais a IA que distingue o FIXDAUTO de qualquer outro software. É a diferença entre gerir a
                oficina e fazer a oficina crescer.
              </p>
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary-300/40 bg-primary-400/15 px-3.5 py-2.5 text-sm font-semibold text-primary-200">
                <span aria-hidden>✓</span>
                30 dias grátis — sem cartão de crédito
              </div>
              <div className="mt-5 flex items-baseline gap-0.5">
                <span className="text-5xl font-extrabold tracking-tight text-white">+{proUpgradeDelta}</span>
                <span className="text-xl font-bold text-primary-300">€</span>
                <span className="text-sm text-white/50">/mês</span>
              </div>
              <p
                className={`mt-1 min-h-[18px] text-sm text-primary-300 transition-opacity ${
                  annual ? 'opacity-100' : 'opacity-0'
                }`}
              >
                650€/ano (poupa 130€)
              </p>
              <hr className="my-5 border-white/10" />
              <PlanFeatureList variant="pro" dark />
              <a
                href="#registo"
                onClick={scrollToRegisto}
                className="btn btn-primary mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold no-underline"
              >
                Testar grátis durante 30 dias
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden strokeWidth={2.25} />
              </a>
              <p className="mt-2 text-center text-xs text-white/40">Sem cartão de crédito. Cancele quando quiser.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function PlanFeatureList({ variant, dark }: { variant: 'base' | 'pro'; dark?: boolean }) {
  const ok = dark ? 'text-primary-400' : 'text-primary-600'
  const text = dark ? 'text-white/80' : 'text-secondary-700'
  const muted = dark ? 'text-white/35' : 'text-secondary-400'
  const ai = dark ? 'text-primary-300' : 'text-primary-700'
  const aiMark = dark ? 'text-primary-300' : 'text-primary-700'
  const diffBox = dark
    ? 'border-primary-400/30 bg-primary-500/10'
    : 'border-primary-200 bg-primary-50'
  const diffLabel = dark ? 'text-primary-300' : 'text-primary-600'

  if (variant === 'base') {
    return (
      <ul className={`mb-6 flex flex-col gap-2.5 text-sm leading-snug ${text}`}>
        {PLAN_BASE_FEATURES_INCLUDED.map((feat) => (
          <li key={feat} className="flex gap-2">
            <span className={`shrink-0 pt-0.5 ${ok}`}>✓</span>
            <span>{feat}</span>
          </li>
        ))}
        {PLAN_BASE_FEATURES_EXCLUDED.map((feat) => (
          <li key={feat} className={`flex gap-2 ${muted} line-through`}>
            <span className="shrink-0 pt-0.5">✕</span>
            <span>{feat}</span>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <>
      <div className={`mb-4 rounded-xl border p-4 ${diffBox}`}>
        <p className={`mb-2.5 text-xs font-bold uppercase tracking-wider ${diffLabel}`}>✦ O que o Pro faz a mais</p>
        <ul className={`flex flex-col gap-2.5 text-sm leading-snug ${text}`}>
          {PLAN_PRO_DIFF_FEATURES.map((feat) => (
            <li key={feat} className="flex gap-2">
              <span className={`shrink-0 pt-0.5 font-bold ${aiMark}`}>✦</span>
              <span className={`font-medium ${ai}`}>{feat}</span>
            </li>
          ))}
        </ul>
      </div>
      <ul className={`mb-6 flex flex-col gap-2.5 text-sm leading-snug ${text}`}>
        {PLAN_PRO_EXTRA_FEATURES.map((feat) => (
          <li key={feat} className="flex gap-2">
            <span className={`shrink-0 pt-0.5 ${ok}`}>✓</span>
            <span>{feat}</span>
          </li>
        ))}
      </ul>
    </>
  )
}

function LandingFaqColumn() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="flex w-full flex-col self-start rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-sm ring-1 ring-white/[0.06] sm:p-8">
      <div className="mb-5 border-b border-white/10 pb-5">
        <p className="mb-2 text-xs font-medium uppercase tracking-widest text-primary-300">FAQ</p>
        <h3 className="text-lg font-semibold text-white sm:text-xl">Respostas às dúvidas mais comuns</h3>
        <p className="mt-1 text-sm text-white/60">Tudo o que precisa de saber antes de começar.</p>
      </div>
      <ul className="flex flex-col gap-2.5">
        {LANDING_FAQS.map((item, i) => {
          const open = openIndex === i
          return (
            <li
              key={item.q}
              className={`overflow-hidden rounded-xl border transition-colors duration-300 ${
                open
                  ? 'border-primary-200 bg-white shadow-sm'
                  : 'border-white/10 bg-white/[0.04]'
              }`}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? null : i)}
              >
                <span
                  className={`text-sm font-semibold leading-snug ${open ? 'text-black' : 'text-white'}`}
                >
                  {item.q}
                </span>
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm transition-transform duration-300 ${
                    open
                      ? 'rotate-45 bg-primary-100 text-primary-700'
                      : 'bg-white/10 text-white'
                  }`}
                >
                  +
                </span>
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-1000 ease-in-out ${
                  open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
                aria-hidden={!open}
              >
                <div className="min-h-0 overflow-hidden">
                  {open && (
                    <div
                      className="mx-6 mb-1 mt-0.5 h-px bg-black"
                      aria-hidden
                    />
                  )}
                  <p
                    className={`px-4 pb-4 pt-2 text-sm leading-relaxed text-black transition-opacity duration-1000 ease-in-out ${
                      open ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {item.a}
                  </p>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function LandingFormSection({ heroVariantIndex }: { heroVariantIndex: number }) {
  const [nome, setNome] = useState('')
  const [oficina, setOficina] = useState('')
  const [email, setEmail] = useState('')
  const [telemovel, setTelemovel] = useState('')
  const [mecanicos, setMecanicos] = useState('')
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState(WORKSHOP_SIGNUP_SUCCESS_MESSAGE)

  const submit = () => {
    if (!nome.trim() || !oficina.trim() || !email.trim() || !telemovel.trim() || !mecanicos) {
      window.alert('Por favor preencha nome, oficina, email, telemóvel e número de mecânicos.')
      return
    }

    const payload = {
      name: nome.trim(),
      workshopName: oficina.trim(),
      email: email.trim(),
      phone: telemovel.trim(),
      mechanicsRange: mecanicos.trim(),
      heroVariant: heroVariantIndex,
    }

    setLoading(true)
    setError(null)
    setSuccessMessage(WORKSHOP_SIGNUP_SUCCESS_MESSAGE)
    setDone(true)
    setLoading(false)
    markLandingConverted()

    // keepalive: o pedido pode concluir mesmo que o utilizador saia da página
    void submitWorkshopSignup(payload).catch(() => {
      setDone(false)
      setError(
        'Não foi possível enviar o pedido. Verifique a ligação à internet e tente novamente, ou contacte equipa.fixdauto@gmail.com.'
      )
    })
  }

  return (
    <section
      id="registo"
      className="relative scroll-mt-24 overflow-hidden bg-gradient-to-b from-[#17253C] via-primary-900 to-[#17253C] px-5 py-16 sm:px-8 sm:py-24"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(14,165,233,0.12),_transparent_55%)]" aria-hidden />
      <div className="relative mx-auto max-w-6xl">
        <div className="mb-10 text-center" data-reveal>
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Preencha o formulário e receba o seu acesso gratuito já hoje.
            <br />
            Sem compromissos.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/75">
            Preencha o formulário e receba o seu acesso gratuito. A equipa da FIXDAUTO trata da migração — só tem de
            começar a usar.
          </p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12" data-reveal>
          <div className="flex w-full flex-col self-start rounded-2xl border border-secondary-200/90 bg-white p-6 text-left shadow-[0_25px_50px_-12px_rgba(15,23,42,0.28)] ring-1 ring-secondary-900/[0.04] sm:p-8 lg:h-[34rem] lg:min-h-[34rem]">
            {!done ? (
              <div className="flex h-full flex-col">
                <header className="mb-5 shrink-0 border-b border-secondary-100 pb-5">
                  <h3 className="text-lg font-semibold text-secondary-900">Registe a sua oficina</h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    30 dias grátis — migração incluída — sem cartão de crédito
                  </p>
                </header>
                <div className="flex flex-col space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="O seu nome" />
                    <Input
                      label="Nome da oficina"
                      value={oficina}
                      onChange={(e) => setOficina(e.target.value)}
                      placeholder="Por exemplo, Auto Reparações Silva"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@oficina.pt"
                    />
                    <Input
                      label="Telemóvel"
                      type="tel"
                      value={telemovel}
                      onChange={(e) => setTelemovel(e.target.value)}
                      placeholder="9XX XXX XXX"
                    />
                  </div>
                  <Select
                    label="Quantos mecânicos tem a sua oficina?"
                    value={mecanicos}
                    onChange={(e) => setMecanicos(e.target.value)}
                  >
                    <option value="">Selecione uma opção</option>
                    <option value="1-2">1 a 2 mecânicos</option>
                    <option value="3-5">3 a 5 mecânicos</option>
                    <option value="6-10">6 a 10 mecânicos</option>
                    <option value="10+">Mais de 10 mecânicos</option>
                  </Select>
                </div>
                {error ? (
                  <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                    {error}
                  </p>
                ) : null}
                <div className="mt-auto shrink-0">
                  <button
                    type="button"
                    className="btn btn-primary mt-6 w-full rounded-xl py-3.5 text-base disabled:opacity-60"
                    disabled={loading}
                    onClick={() => void submit()}
                  >
                    {loading ? 'A enviar…' : 'Experimentar grátis →'}
                  </button>
                  <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-secondary-500">
                    <Lock className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden strokeWidth={2} />
                    Os seus dados estão seguros. Não enviamos spam.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col justify-center py-8 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-2xl text-primary-600">
                  ✓
                </div>
                <div className="mb-2 text-xl font-semibold text-secondary-900">Registo concluído!</div>
                <p className="text-sm leading-relaxed text-secondary-500">{successMessage}</p>
              </div>
            )}
          </div>

          <LandingFaqColumn />
        </div>
      </div>
    </section>
  )

}
